/**
 * Event names and parameter vocabularies for the Pi landing page. Kept identical to the
 * `PiEventTracker` schema in gcms-admin so both surfaces report into the same GA definitions.
 */

export enum PiLandingEventType {
  PAGE_VIEWED = "pi_landing_page_viewed",
  SECTION_VIEWED = "pi_landing_section_viewed",
  CTA_CLICKED = "pi_landing_cta_clicked",
  LOGIN_ACTION = "pi_landing_login_action",
  EXAM_SELECTED = "pi_landing_exam_selected",
  FAQ_TOGGLED = "pi_landing_faq_toggled",
  PURCHASE_BUTTON_CLICKED = "pi_purchase_button_clicked",
}

export const PI_LANDING_PAGE_NAME = "pi_landing_page";
export const PI_LANDING_VERSION = "v1";

export const PI_CTA_NAME = {
  LOGIN: "login",
  START_LEARNING_FREE: "start_learning_free",
  SEE_PLANS: "see_plans",
} as const;

export const PI_CTA_LOCATION = {
  HEADER: "header",
  HERO: "hero",
  FINAL_SECTION: "final_section",
  STICKY_MOBILE: "sticky_mobile",
} as const;

export const PI_DESTINATION = {
  LOGIN_MODAL: "login_modal",
  PW_HOME: "pw_home",
  PRICING_SECTION: "pricing_section",
} as const;

export const PI_EXPOSURE_SOURCE = {
  SCROLL: "scroll",
  SEE_PLANS_CTA: "see_plans_cta",
} as const;

/** One-based visual order of the landing sections. */
export const PI_SECTION = {
  EXAM_SELECTOR: { name: "exam_selector", position: 2 },
  FEATURES: { name: "features", position: 3 },
  COMPARISON: { name: "comparison", position: 4 },
  PRICING: { name: "pricing", position: 5 },
  TESTIMONIALS: { name: "testimonials", position: 6 },
  FAQ: { name: "faq", position: 7 },
} as const;

/** Login entry points, one per CTA that can open the auth modal. */
export const PI_LOGIN_ENTRY_POINT = {
  HEADER_LOGIN: "header_login",
  HEADER_START_LEARNING: "header_start_learning",
  HERO_START_FREE: "hero_start_free",
  FINAL_CTA_START_LEARNING: "final_cta_start_learning",
  STICKY_MOBILE_START_LEARNING: "sticky_mobile_start_learning",
  PRICING_PLAN_CARD: "pricing_plan_card",
} as const;

export type PiCtaName = (typeof PI_CTA_NAME)[keyof typeof PI_CTA_NAME];
export type PiCtaLocation =
  (typeof PI_CTA_LOCATION)[keyof typeof PI_CTA_LOCATION];
export type PiDestination =
  (typeof PI_DESTINATION)[keyof typeof PI_DESTINATION];
export type PiExposureSource =
  (typeof PI_EXPOSURE_SOURCE)[keyof typeof PI_EXPOSURE_SOURCE];
export type PiLoginEntryPoint =
  (typeof PI_LOGIN_ENTRY_POINT)[keyof typeof PI_LOGIN_ENTRY_POINT];
export type PiCloseMethod = "close_icon" | "backdrop" | "escape";
export type PiLoginAction =
  | "modal_viewed"
  | "get_otp_clicked"
  | "otp_sent"
  | "verify_clicked"
  | "login_succeeded"
  | "login_failed"
  | "resend_otp_clicked"
  | "mobile_edit_clicked"
  | "modal_closed";
